import React, { useEffect, useState } from 'react';
import notify from '../../utils/notifier';
import LocationsService from '../../services/LocationsService';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PaginationComponent from './../PaginationComponent';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton } from '@mui/material';


const downloadUnlocode = async (countryIso2 = 'IN', type = '') => {
   console.log('Downloading unlocodes for country:', countryIso2, 'and type:', type);
    const query = { _countryIso2: countryIso2 };
    if (type) query._type = type;

    try {
      if(query._format === undefined || !query._format){
        query._format = 'CSV'
      }

      const response = await LocationsService.downloadUnlocodeData(query);
      console.log('Download response:', response.data);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      
      const timestamp = new Date().toLocaleString('en-GB').replace(/[/,: ]/g, '-');
      const fileName = `unlocodes_${timestamp}.csv`;
      
      link.download = fileName; 
      link.click();
      setTimeout(() => {
        notify({
          type: 'success',
          message: 'File downloaded successfully.'
        });
      }, 50); 
    } catch (error) {
      console.error('Failed to download file:', error);
      notify({
        type: 'error',
        message: 'Something went wrong while downloading the file.'
      });
    }
};

const fetchUnlocodes = async (countryIso2 = 'IN', type = '') => {
  let output = [];
  try {
    const query = { _countryIso2: countryIso2 };
    if (type) query._type = type;
    const response = await LocationsService.getUnlocodeData(query);
    response.data.forEach(unlocode => {
      output.push(unlocode);
    });
  } catch (error) {
    notify({
      type: 'error',
      message: error.message
    });
  }
  return output;
};

const fetchCountries = async () => {
  let output = [];
  try {
    const response = await LocationsService.getCountriesData();
    response.data.forEach(country => {
      output.push(country);
    });
  } catch (error) {
    notify({
      type: 'error',
      message: 'Internal Server Error.'
    });
  }
  return output;
};

const columns = [
  { id: 'code', label: 'UNLOCODE' },
  { id: 'name', label: 'Name' },
  { id: 'type', label: 'Type' },
  { id: 'countryName', label: 'Country Name' },
  { id: 'countryIso2', label: 'Country ISO2' },
];

const unlocodeTypes = [
  { value: '', label: 'All Types' },
  { value: 'SEA_TERMINAL', label: 'SEA_TERMINAL' },
  { value: 'AIR_TERMINAL', label: 'AIR_TERMINAL' },
  { value: 'RAIL_TERMINAL', label: 'RAIL_TERMINAL' },
  { value: 'ROAD_TERMINAL', label: 'ROAD_TERMINAL' },
  { value: 'ICD', label: 'ICD' }
];

const StaticUnlocodesTable = () => {
  const [unlocodes, setUnlocodes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedCountry, setSelectedCountry] = useState('IN'); // Default to India
  const [selectedType, setSelectedType] = useState(''); // Optional, default empty
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCountries().then(setCountries);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchUnlocodes(selectedCountry, selectedType).then((data) => {
      setUnlocodes(data);
      setLoading(false);
    });
  }, [selectedCountry, selectedType]);

  useEffect(() => {
    setFiltered(
      unlocodes.filter(
        u =>
          (u.code && u.code.toLowerCase().includes(search.toLowerCase())) ||
          (u.name && u.name.toLowerCase().includes(search.toLowerCase())) ||
          (u.type && u.type.toLowerCase().includes(search.toLowerCase())) ||
          (u.countryName && u.countryName.toLowerCase().includes(search.toLowerCase())) ||
          (u.countryIso2 && u.countryIso2.toLowerCase().includes(search.toLowerCase()))
      )
    );
    setPage(0); // Reset to first page when searching
  }, [search, unlocodes]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
    setSearch(''); // Clear search when country changes
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
    setSearch(''); // Clear search when type changes
  };

  // Calculate paginated data
  const paginatedData = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3, backgroundColor: '#F2F2F2', minHeight: 'calc(100vh - 144px)' }}>
      <Paper sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#298BC1' }}>
              UNLOCODES
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select
                value={selectedCountry}
                label="Country"
                onChange={handleCountryChange}
              >
                {countries.map((country) => (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    {country.name} ({country.iso2})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{selectedType == '' ? '' : 'Type'}</InputLabel>
              <Select
                value={selectedType}
                label="Type"
                onChange={handleTypeChange}
                displayEmpty
              >
                {unlocodeTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                variant="outlined"
                placeholder="Search by unlocode, name, type, country, ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                    onClick={() => downloadUnlocode(selectedCountry, selectedType)}   
                    sx={{
                      backgroundColor: '#298BC1',
                      color: 'white',
                      '&:hover': { backgroundColor: '#2477a9' },
                      borderRadius: '6px',
                      p: 1.2
                    }}
                  >
                  <DownloadIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: 'none', 
            border: '1px solid #e0e0e0',
            maxHeight: 'calc(100vh - 300px)',
            overflow: 'auto'
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {columns.map(col => (
                  <TableCell 
                    key={col.id} 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#333',
                      borderBottom: '2px solid #e0e0e0',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#666' }}>
                    Loading unlocodes...
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {paginatedData.map(row => (
                    <TableRow 
                      key={row.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                      }}
                    >
                      {columns.map(col => (
                        <TableCell 
                          key={col.id}
                          sx={{ 
                            borderBottom: '1px solid #e0e0e0',
                            color: '#333'
                          }}
                        >
                          {row[col.id]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {paginatedData.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#666' }}>
                        No unlocodes found.
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2 }}>
          <PaginationComponent
            queryCount={filtered.length}
            page={page}
            handleChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default StaticUnlocodesTable; 